/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions,
react/no-unstable-nested-components */
import React, { useEffect, useRef, useState } from 'react';
import {
  Avatar, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl,
} from '@mui/material';
import ReactCrop, { centerCrop, makeAspectCrop, type Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Accept, useDropzone } from 'react-dropzone';
import { toBase64 } from '../../../utils/fileUtils';
import { CloseIcon } from '../../../icons';
import ChangeIcon from '../../../icons/ChangeIcon';
import { CommonElementProps } from '../types';
import { FileServerValue, getErrorMessage } from '../File';

interface CropImageComponentRef {
  onCropComplete: () => Promise<File>;
}

interface CropImageProps {
  src: string;
}
const CropImage = React.forwardRef<CropImageComponentRef, CropImageProps>(
  (props: CropImageProps, ref) => {
    const { src } = props;
    const [crop, setCrop] = useState<Crop>({
      unit: 'px',
      x: 25,
      y: 25,
      width: 50,
      height: 50,
    });

    const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      const { naturalWidth: width, naturalHeight: height } = e.currentTarget;

      const cropDefault = centerCrop(
        makeAspectCrop(
          {
            unit: '%',
            width: 90,
          },
          1,
          width,
          height,
        ),
        width,
        height,
      );
      setCrop(cropDefault);
    };

    const onCropComplete = (): Promise<File> => new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const imageElement = document.getElementById('cropImage') as HTMLImageElement;
      if (!imageElement) return;

      const scaleX = imageElement.naturalWidth / imageElement.width;
      const scaleY = imageElement.naturalHeight / imageElement.height;

      canvas.width = crop.width || 0;
      canvas.height = crop.height || 0;

      const context = canvas.getContext('2d');
      if (!context) return;

      context.drawImage(
        imageElement,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        crop.width,
        crop.height,
      );

      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], 'croppedImage.png', {
            type: 'image/png',
            lastModified: Date.now(),
          });
          resolve(file);
        }
      }, 'image/png');
    });

    React.useImperativeHandle(ref, () => ({
      onCropComplete,
    }));

    return (
      <ReactCrop
        keepSelection
        aspect={1}
        crop={crop}
        minWidth={50}
        circularCrop
        minHeight={50}
        onChange={(c) => setCrop(c)}
      >
        <img
          src={src}
          id="cropImage"
          crossOrigin="anonymous"
          alt="edit"
          onLoad={onImageLoad}
          className="max-h-[50vh]"
        />
      </ReactCrop>
    );
  },
);

export enum ImageFormat {
  PNG = 'image/png',
  JPG = 'image/jpeg',
}

export type ImageValueType = string | File | FileServerValue | null | undefined;

export interface ImageUploadProps extends CommonElementProps<ImageValueType> {
  minSize: number;
  maxSize: number;
  supportTypes: Accept;
  isMandatory?: boolean;
  onChange?: (file: ImageValueType) => void;
  hideClose?: boolean;
  styles?: {
    avatar: React.CSSProperties,
  }
}
const ImageUpload = function ImageUpload(props: ImageUploadProps) {
  const {
    // label,
    maxSize = 1024 * 1024,
    minSize = 500 * 1024,
    supportTypes,
    // isMandatory,
    onChange,
    // error,
    value: defaultValue,
    hideClose,
    styles,
  } = props;
  const [open, setOpen] = useState<string | null>(null);
  const [hover, setHover] = useState(false);

  const [value, setValue] = useState<ImageValueType>(defaultValue);

  useEffect(() => {
    const setConvertedValue = async (fileData: File) => {
      const base64File = await toBase64(fileData);
      setValue(base64File);
    };

    if (defaultValue instanceof File) {
      setConvertedValue(defaultValue);
    } else {
      setValue(defaultValue);
    }
  }, [defaultValue]);

  const cropRef = useRef<CropImageComponentRef>(null);
  const onFileSelect = async (file: File) => {
    const fileUrl = await toBase64(file);
    setOpen(fileUrl);
  };

  const {
    fileRejections,
    getRootProps,
    getInputProps,
  } = useDropzone({
    accept: supportTypes,
    multiple: false,
    minSize,
    maxSize,
    maxFiles: 1,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        onFileSelect(acceptedFiles[0]);
      }
    },
    onFileDialogCancel: () => {
      if (onChange) {
        // onChange(null);
      }
    },
    onDropRejected: () => {
      if (onChange) {
        onChange(null);
      }
    },
  });

  const fileRejectionItems = fileRejections.map(({ file, errors }) => (
    <div className="flex flex-col text-left" key={file.name}>
      <div>
        <div className="text-[10px] text-[red]">
          {getErrorMessage(errors, minSize, maxSize)}
        </div>
      </div>
    </div>
  ));

  const onClickOk = async () => {
    if (cropRef.current) {
      const croppedFile = await cropRef.current.onCropComplete();
      setOpen(null);
      if (onChange) {
        onChange(croppedFile);
      }
    }
  };

  return (
    <>
      <FormControl className="relative">
        {/* <InputLabel error={!!error} required={isMandatory}
        className="text-[14px] font-semibold -ml-3 -mt-[10px]
        text-[#28364C]">{label}</InputLabel> */}
        {
          value && !hideClose && (
            <div
              className="absolute z-10 right-[-4px] top-[-4px]"
              onClick={() => {
                if (onChange) {
                  onChange(null);
                }
              }}
            >
              <CloseIcon className="cursor-pointer bg-white rounded-full" />
            </div>
          )
        }
        <div {...getRootProps({ className: 'dropzone cursor-pointer' })}>
          <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} className="rounded-full outline outline-[#EAEBED] relative">
            <Avatar src={(value as FileServerValue)?.presingnedUrl || (value as string)} sx={styles?.avatar || { width: '60px', height: '60px' }} />
            {
              hover && <ChangeIcon className="absolute top-0 left-0 right-0 bottom-0" style={styles?.avatar || { width: '60px', height: '60px' }} />
            }
          </div>
          <input {...getInputProps()} />
        </div>
        <div className="absolute -bottom-[60%]">{fileRejectionItems}</div>
      </FormControl>
      <Dialog
        open={Boolean(open)}
        aria-describedby="alert-dialog-slide-description"
        transitionDuration={{
          enter: 500,
          exit: 500,
        }}
        onClose={() => setOpen(null)}
      >
        <div className="absolute right-2 top-2" onClick={() => setOpen(null)}>
          <CloseIcon className="cursor-pointer" />
        </div>
        <DialogTitle sx={{ textAlign: 'center' }}>Crop Image</DialogTitle>
        <DialogContent>
          <CropImage ref={cropRef} src={open as string} />
        </DialogContent>
        <DialogActions
          sx={{
            marginBottom: 2,
            justifyContent: 'center',
          }}
        >
          <Button
            color="primary"
            variant="outlined"
            size="large"
            onClick={() => setOpen(null)}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            size="large"
            onClick={onClickOk}
          >
            Ok
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

ImageUpload.defaultProps = {
  isMandatory: false,
  onChange: () => { },
  hideClose: false,
  styles: undefined,
};

ImageUpload.propTypes = {

};
export default ImageUpload;
